/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import type {
  InputCandidate,
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { StyledContainerWrapper } from "../../../style";
import {
  StyledChecklistSection,
  StyledTaskControllers,
  StyledTaskSection,
  StyledTrashIcon,
  StyledDragIcon,
  StyledHr,
  StyledButtonWrapper,
} from "./style";
import { AddButton, Icon, IconButton } from "@smartsuite/react-ui/lib";
import { v4 as uuid } from "uuid";
import type { DropResult } from "react-beautiful-dnd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDraggableInPortal } from "../../../../../../../../hooks/use-draggable-in-portal";
import { CheckListItem } from "./CheckListItem";
import type { ChecklistItemConfigCandidate } from "../../../../../../../../types/checklist";
import {
  getCandidatesFromChecklistItemConfigs,
  getHashFromChecklistItemConfig,
} from "./utils";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { SolutionColorVariables, TextColorNames } from "@smartsuite/types";

export const ChecklistEditor = observer<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  onClickTrash?: () => void;
  description?: InputDescription;
  integrations: IntegrationDescription[];
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  showValidationError: boolean;
}>(
  ({
    description,
    onChange,
    onClickTrash,
    input,
    integrations,
    actions,
    action,
    trigger,
    showValidationError,
  }) => {
    const [isDragInProgress, setIsDragInProgress] = useState<boolean>(false);
    const renderDraggable = useDraggableInPortal();
    const store = useRootStore();
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const onClickTaskTrash = (index: number): void => {
      if (
        input.value !== undefined &&
        input.value.values[0].value.oneofKind === "checklist"
      ) {
        if (input.value.values[0].value.checklist.itemsToAdd.length > 1) {
          input.value.values[0].value.checklist.itemsToAdd =
            input.value.values[0].value.checklist.itemsToAdd.filter(
              (item, ind) => ind !== index,
            );
          onChange(input);
        }
      }
    };
    const onDragStart = (): void => {
      setIsDragInProgress(true);
    };
    const onDragEnd = (result: DropResult): void => {
      setIsDragInProgress(false);
      if (
        result.destination !== undefined &&
        result.destination != null &&
        result.destination.index !== result.source.index
      ) {
        if (
          input.value !== undefined &&
          input.value.values[0].value.oneofKind === "checklist"
        ) {
          input.value.values[0].value.checklist.itemsToAdd = getMovedArray(
            getCandidatesFromChecklistItemConfigs(
              input.value.values[0].value.checklist.itemsToAdd,
            ),
            result.source.index,
            result.destination.index,
          );
          onChange(input);
        }
      }
    };

    const getMovedArray = (
      arr: ChecklistItemConfigCandidate[],
      oldIndex: number,
      newIndex: number,
    ): ChecklistItemConfigCandidate[] => {
      const item = arr.slice(oldIndex, oldIndex + 1)[0];
      const newArr: ChecklistItemConfigCandidate[] = arr.filter(
        (i, ind) => ind !== oldIndex,
      );
      newArr.splice(newIndex, 0, item);
      return newArr;
    };

    useEffect(() => {
      if (input.value === undefined || input.value.values.length === 0) {
        const checklistItemInitial: ChecklistItemConfigCandidate = {
          hash: uuid(),
          text: {
            values: [
              {
                value: {
                  oneofKind: "string",
                  string: "",
                },
              },
            ],
          },
        };
        input.value = {
          values: [
            {
              value: {
                oneofKind: "checklist",
                checklist: {
                  percentageComplete: 0,
                  itemsToAdd: [checklistItemInitial],
                },
              },
            },
          ],
        };
        store.inputToFocus = {
          ...input,
          inputId: checklistItemInitial.hash,
        };
        onChange(input);
      } else {
        if (input.value.values[0].value.oneofKind !== "checklist") {
          return;
        }
        input.value.values[0].value.checklist.itemsToAdd =
          input.value.values[0].value.checklist.itemsToAdd.map((item) => {
            return {
              ...item,
              hash: uuid(),
            };
          });
        onChange(input);
      }
    }, []);

    const addChecklistItem = (): void => {
      if (
        input.value !== undefined &&
        input.value.values[0].value.oneofKind === "checklist" &&
        input.value.values[0].value.checklist.itemsToAdd.length <
          store.featureFlags.limits.maxChecklistItems
      ) {
        const checklistItemInitial: ChecklistItemConfigCandidate = {
          hash: uuid(),
          text: {
            values: [
              {
                value: {
                  oneofKind: "string",
                  string: "",
                },
              },
            ],
          },
        };
        store.inputToFocus = {
          ...input,
          inputId: checklistItemInitial.hash,
        };
        input.value.values[0].value.checklist.itemsToAdd = [
          ...input.value.values[0].value.checklist.itemsToAdd,
          checklistItemInitial,
        ];
        onChange(input);
      }
    };

    const changeActionType = (
      actionType:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      if (actionType === undefined) return;

      onChange({
        ...input,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : input.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    };

    return (
      <StyledContainerWrapper>
        {description !== undefined && (
          <CaptionBlock
            description={description}
            onClickTrash={onClickTrash}
            isOperatorSelect
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description?.operators?.operators.length > 0 && (
                <UpdateValueDropdown
                  operatorConfig={{
                    operator: {
                      oneofKind: "append",
                      append: {},
                    },
                  }}
                  operatorDescriptions={description?.operators.operators}
                  onOpenedChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
                  onChange={changeActionType}
                  inCaptionBlock
                  disabled
                />
              )}
          </CaptionBlock>
        )}

        <StyledChecklistSection>
          <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <Droppable droppableId="action-droppable">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ position: "relative" }}
                >
                  {input.value !== undefined &&
                    input.value.values.length > 0 &&
                    input.value.values[0].value.oneofKind === "checklist" &&
                    input.value.values[0].value.checklist.itemsToAdd.map(
                      (item, index) => {
                        const hash = getHashFromChecklistItemConfig(item);
                        if (hash === undefined) {
                          return null;
                        }
                        return (
                          <div key={hash}>
                            <Draggable draggableId={hash} index={index}>
                              {renderDraggable((provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <StyledTaskSection
                                    isDragInProgress={isDragInProgress}
                                  >
                                    <StyledTaskControllers>
                                      <StyledTrashIcon>
                                        <IconButton
                                          className="trash-icon"
                                          size="S"
                                          color="currentColor"
                                          hoverColor="currentColor"
                                          name="trash-duotone"
                                          ariaLabel="Remove row"
                                          onClick={() =>
                                            onClickTaskTrash(index)
                                          }
                                        />
                                      </StyledTrashIcon>
                                      <StyledDragIcon
                                        {...provided.dragHandleProps}
                                      >
                                        <Icon
                                          className="drag-icon"
                                          size="M"
                                          color="currentColor"
                                          name="drag"
                                          transition
                                        />
                                      </StyledDragIcon>
                                    </StyledTaskControllers>
                                    <CheckListItem
                                      onChange={(
                                        newValue: ChecklistItemConfigCandidate,
                                      ) => {
                                        if (
                                          input.value !== undefined &&
                                          input.value.values[0].value
                                            .oneofKind === "checklist"
                                        ) {
                                          input.value.values[0].value.checklist.itemsToAdd =
                                            input.value.values[0].value.checklist.itemsToAdd.map(
                                              (val) => {
                                                return getHashFromChecklistItemConfig(
                                                  val,
                                                ) === newValue.hash
                                                  ? { ...val, ...newValue }
                                                  : { ...val };
                                              },
                                            );
                                          onChange(input);
                                        }
                                      }}
                                      hash={hash}
                                      description={description}
                                      integrations={integrations}
                                      trigger={trigger}
                                      actions={actions}
                                      action={action}
                                      showValidationError={
                                        showValidationError ||
                                        !isInputValidByFieldMissing(
                                          input,
                                          action,
                                        )
                                      }
                                      input={input}
                                      value={item}
                                    />
                                  </StyledTaskSection>
                                </div>
                              ))}
                            </Draggable>
                          </div>
                        );
                      },
                    )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <StyledHr />
          <StyledButtonWrapper>
            <AddButton
              onClick={addChecklistItem}
              iconStyle={{
                name: "plus",
                color: SolutionColorVariables.sc1,
                hoverColor: SolutionColorVariables.sc1,
                marginRight: 10,
              }}
              textStyle={{
                size: "s",
                weight: "medium",
                color: TextColorNames.secondary,
                hoverColor: TextColorNames.primary,
              }}
            >
              Add Checklist Item
            </AddButton>
          </StyledButtonWrapper>
        </StyledChecklistSection>
        <FieldMissingError
          message={getFieldMissingErrorMessage(input, action)}
        />
      </StyledContainerWrapper>
    );
  },
);
